import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, Code, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import UserMenu from './auth/UserMenu';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import AuthForm from './auth/AuthForm';
import { CreditBalance } from './CreditBalance';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const location = useLocation();
  const { currentUser } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <header 
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? 'py-3 bg-background/80 backdrop-blur-lg border-b border-white/10' : 'py-4'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-purple-500" />
            <span className="font-bold text-xl">PromptAssist</span>
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            <Link 
              to="/" 
              className={`text-sm transition-colors hover:text-white ${
                location.pathname === '/' ? 'text-white font-medium' : 'text-white/70'
              }`}
            >
              Home
            </Link>
            <a 
              href="#features" 
              className="text-sm text-white/70 transition-colors hover:text-white"
            >
              Features
            </a>
            <a 
              href="#how-it-works" 
              className="text-sm text-white/70 transition-colors hover:text-white"
            >
              How It Works
            </a>
            <Link 
              to="/pricing" 
              className={`text-sm transition-colors hover:text-white ${
                location.pathname === '/pricing' ? 'text-white font-medium' : 'text-white/70'
              }`}
            >
              Pricing
            </Link>
            {currentUser && (
              <Link 
                to="/history" 
                className={`text-sm transition-colors hover:text-white ${
                  location.pathname === '/history' ? 'text-white font-medium' : 'text-white/70'
                }`}
              >
                History
              </Link>
            )}
          </nav>

          <div className="hidden md:flex items-center gap-4">
            {currentUser && <CreditBalance />}
            {currentUser ? (
              <UserMenu />
            ) : (
              <Dialog open={isAuthOpen} onOpenChange={setIsAuthOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:opacity-90 transition-opacity">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Sign Up
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Sign in to PromptAssist</DialogTitle>
                    <DialogDescription>
                      Get started with PromptAssist to generate amazing AI prompts
                    </DialogDescription>
                  </DialogHeader>
                  <div className="py-4">
                    <AuthForm onSuccess={() => setIsAuthOpen(false)} />
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>

          <button
            className="block md:hidden text-white/80 hover:text-white"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{
          opacity: isMenuOpen ? 1 : 0,
          height: isMenuOpen ? 'auto' : 0
        }}
        transition={{ duration: 0.3 }}
        className="md:hidden overflow-hidden bg-secondary/50 backdrop-blur-lg border-b border-white/10"
      >
        {isMenuOpen && (
          <div className="container mx-auto px-4 py-4 flex flex-col gap-4">
            <Link
              to="/"
              className={`py-2 ${
                location.pathname === '/' ? 'text-white font-medium' : 'text-white/70'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Home
            </Link>
            <a
              href="#features"
              className="py-2 text-white/70"
              onClick={() => setIsMenuOpen(false)}
            >
              Features
            </a>
            <a
              href="#how-it-works"
              className="py-2 text-white/70"
              onClick={() => setIsMenuOpen(false)}
            >
              How It Works
            </a>
            <Link
              to="/pricing"
              className={`py-2 ${
                location.pathname === '/pricing' ? 'text-white font-medium' : 'text-white/70'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Pricing
            </Link>
            {currentUser && (
              <>
                <Link
                  to="/history"
                  className={`py-2 ${
                    location.pathname === '/history' ? 'text-white font-medium' : 'text-white/70'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  History
                </Link>
                <div className="py-2">
                  <CreditBalance />
                </div>
              </>
            )}
            <div className="pt-2">
              {currentUser ? (
                <div className="flex items-center justify-between">
                  <div className="text-sm text-white/70">
                    Signed in as {currentUser.displayName || currentUser.email}
                  </div>
                  <button 
                    onClick={() => {
                      const { signOut } = useAuth();
                      signOut();
                      setIsMenuOpen(false);
                    }}
                    className="text-red-500 text-sm"
                  >
                    Sign out
                  </button>
                </div>
              ) : (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button 
                      className="w-full bg-gradient-to-r from-indigo-600 to-purple-600"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      Sign Up
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Sign in to PromptAssist</DialogTitle>
                      <DialogDescription>
                        Get started with PromptAssist to generate amazing AI prompts
                      </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                      <AuthForm />
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        )}
      </motion.div>
    </header>
  );
};

export default Navbar;
